import { useEffect, useRef } from 'react';
import Intercom, {
  update as updateIntercom,
  shutdown as shutdownIntercom,
  boot as bootIntercom,
} from '@intercom/messenger-js-sdk';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hooks';
import { DEFAULT_LANGUAGE, SUPPORTED_LANG_CODES } from 'i18n/config';

const intercomAppId = process.env.REACT_APP_INTERCOM_APP_ID || 'krohmykn';

const getInitialLanguage = (): string => {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('language');
    if (saved && (SUPPORTED_LANG_CODES as readonly string[]).includes(saved)) {
      return saved;
    }
  }
  return DEFAULT_LANGUAGE;
};

if (intercomAppId && typeof window !== 'undefined') {
  Intercom({ app_id: intercomAppId, language_override: getInitialLanguage() });
}

export const useIntercom = () => {
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { i18n } = useTranslation();
  const currentLang = (i18n.language?.slice(0, 2) as string) || DEFAULT_LANGUAGE;

  const currentLangRef = useRef(currentLang);
  const isFirstRender = useRef(true);

  useEffect(() => {
    currentLangRef.current = currentLang;
  }, [currentLang]);

  useEffect(() => {
    const handleResize = () => {
      if (intercomAppId) {
        updateIntercom({ hide_default_launcher: window.innerWidth <= 1024 });
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!intercomAppId) return;

    // Shut down the previous session to ensure a clean state
    shutdownIntercom();

    if (isAuthenticated && user) {
      // Boot an authenticated session. Identity travels inside the JWT the API
      // signs (GET /client/profile → intercomUserJwt), so `user_id` and
      // `email` are deliberately NOT repeated here: anything outside the token
      // is editable from the browser console, which is what Intercom's
      // "unprotected attributes" check flags. `name` stays — display-only.
      bootIntercom({
        app_id: intercomAppId,
        name: user.username,
        ...(user.intercomUserJwt && {
          intercom_user_jwt: user.intercomUserJwt,
        }),
        // The JWT itself lives an hour; this is how long the Messenger session
        // stays valid after boot, and it is re-booted on every page load.
        session_duration: 86_400_000,
        hide_default_launcher: window.innerWidth <= 1024,
        language_override: currentLangRef.current,
      });
    } else if (!isAuthenticated) {
      // Boot a guest/anonymous session
      bootIntercom({
        app_id: intercomAppId,
        hide_default_launcher: window.innerWidth <= 1024,
        language_override: currentLangRef.current,
      });
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    if (!intercomAppId) return;

    // Avoid redundant update on initial mount since bootIntercom already applied currentLang
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    updateIntercom({
      language_override: currentLang,
    });
  }, [currentLang]);
};
